import { z } from 'zod';
import { nlwTiers } from '~/util/types/nlw';
import { allTags, Tag } from '~/util/types/tags';

const allTagsSet = new Set(allTags);

const rangeSchema = z.object({
    property: z.enum(['position', 'edel_enjoyment', 'gddl_tier']),
    range: z.object({
        min: z.number().optional(),
        max: z.number().optional(),
    }),
});

const rangesSchema = z.preprocess((array) => {
    if (!Array.isArray(array)) return [];
    return array.filter((entry) => rangeSchema.safeParse(entry).success);
}, z.array(rangeSchema).default([]).catch([]));

const defaultRanges = rangesSchema.parse([]);

const tagsSchema = z.preprocess(
    (tags) => {
        if (typeof tags !== 'object' || tags === null) return {};
        const out: Partial<Record<Tag, boolean>> = {};
        for (const [key, value] of Object.entries(tags)) {
            if (allTagsSet.has(key as Tag) && typeof value === 'boolean') {
                out[key as Tag] = value;
            }
        }
        return out;
    },
    z.partialRecord(z.enum(allTags), z.boolean()).default({}).catch({}),
);

const defaultTags = tagsSchema.parse({});

const selectedLevelSchema = z.object({
    level: z.string(),
    weight: z.number().min(1).default(1).catch(1),
});

export const filtersParamsSchema = z.object({
    ranges: rangesSchema.default(defaultRanges).catch(defaultRanges),
    tags: tagsSchema.default(defaultTags).catch(defaultTags),
    tiers: z.array(z.enum(nlwTiers)).default([]).catch([]),
});

const defaultFilters = filtersParamsSchema.parse({});

export type filtersParamsType = z.infer<typeof filtersParamsSchema>;

export const wheelSpinnerParamsSchema = z.object({
    search: z.string().max(100).default('').catch(''),
    sort: z.enum(['rank', 'enjoyment']).default('rank').catch('rank'),
    reverse: z.boolean().default(false).catch(false),
    filters: filtersParamsSchema.default(defaultFilters).catch(defaultFilters),
    selected_levels: z
        .array(selectedLevelSchema)
        .default([])
        .catch([])
        .transform((levels) => {
            const selectedIds = new Set<string>();
            return levels.filter(({ level }) => {
                if (selectedIds.has(level)) return false;
                selectedIds.add(level);
                return true;
            });
        }),
});

export type wheelSpinnerParamsType = z.infer<typeof wheelSpinnerParamsSchema>;

export const defaultWheelSpinnerParams = wheelSpinnerParamsSchema.parse({});
